import { Session, SupabaseClient, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient'; // Use singleton
import { registrationCompletedSessionKey, trackEventOncePerSession } from '../lib/analytics';
import { Database } from '../types/database.types';
import { getTimezoneForCountry } from '../utils/timezoneUtils';
import { getTrialEndDate } from '../utils/trial';
import { isPasswordPolicyRejection } from '../utils/passwordPolicy';

// Authentication event types for logging
export enum AuthEvent {
  ATTEMPT_LOGIN = 'ATTEMPT_LOGIN',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILURE = 'LOGIN_FAILURE',
  ATTEMPT_SIGNUP = 'ATTEMPT_SIGNUP',
  SIGNUP_SUCCESS = 'SIGNUP_SUCCESS',
  SIGNUP_FAILURE = 'SIGNUP_FAILURE',
  LOGOUT = 'LOGOUT',
  SESSION_REFRESH = 'SESSION_REFRESH',
  SESSION_ERROR = 'SESSION_ERROR'
}

// Detailed error types for better error handling
export enum AuthErrorType {
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  EMAIL_NOT_CONFIRMED = 'EMAIL_NOT_CONFIRMED',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  USER_EXISTS = 'USER_EXISTS',
  WEAK_PASSWORD = 'WEAK_PASSWORD',
  INVALID_EMAIL = 'INVALID_EMAIL',
  MISSING_FIELDS = 'MISSING_FIELDS',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  CONFLICT = 'CONFLICT'
}

// Authentication error with detailed information
export interface AuthError {
  type: AuthErrorType;
  message: string;
  originalError?: any;
  context?: Record<string, any>;
}

// Authentication response interface
export interface AuthResponse<T = any> {
  success: boolean;
  data?: T;
  error?: AuthError;
  statusCode?: number;
}

// Login parameters
export interface LoginParams {
  email: string;
  password: string;
}

export interface LegalAcceptanceParams {
  accepted: boolean;
  locale?: string | null;
  source: 'mobile' | 'web';
}

// Registration parameters
export interface RegisterParams {
  salonName: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  password: string;
  legalAcceptance: LegalAcceptanceParams;
}

// Password reset parameters
export interface PasswordResetParams {
  email: string;
}

// Password verification parameters
export interface PasswordVerifyParams {
  token: string;
  email: string;
}

// Password update parameters
export interface PasswordUpdateParams {
  token: string;
  email: string;
  password: string;
}

/**
 * Supabase Authentication Middleware
 * 
 * Handles all authentication operations with comprehensive error handling,
 * logging, and rate limit management.
 */
export class AuthMiddleware {
  private supabase: SupabaseClient<Database>;
  private rateLimitCache: Map<string, { count: number, timestamp: number }> = new Map();
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly RATE_LIMIT_WINDOW_MS = 60000; // 1 minute
  private readonly RATE_LIMIT_MAX_ATTEMPTS = 5;
  private readonly DEBUG_MODE: boolean;
  private signingUp = false; // guard against concurrent signups

  /**
   * Initialize the authentication middleware with singleton client
   * @param debug Enable debug logging
   */
  constructor(debug: boolean = false) {
    this.supabase = supabase; // Use singleton instead of creating new client
    this.DEBUG_MODE = debug;
    this.logEvent(AuthEvent.SESSION_REFRESH, { message: 'Auth middleware initialized' });
  }

  /**
   * Log authentication events for debugging
   * @param event The authentication event
   * @param data Additional event data
   */
  private logEvent(event: AuthEvent, data?: any): void {
    if (!this.DEBUG_MODE) return;

    const timestamp = new Date().toISOString();
    console.log(`[AUTH][${timestamp}][${event}]`, data || '');
  }

  /**
   * Check if an operation is rate limited
   * @param key The rate limit key (usually operation + identifier)
   * @returns Whether the operation is rate limited
   */
  private isRateLimited(key: string): boolean {
    const now = Date.now();
    const entry = this.rateLimitCache.get(key);

    // Clean up expired entries
    for (const [cacheKey, cacheEntry] of this.rateLimitCache.entries()) {
      if (now - cacheEntry.timestamp > this.RATE_LIMIT_WINDOW_MS) {
        this.rateLimitCache.delete(cacheKey);
      }
    }

    if (!entry) {
      this.rateLimitCache.set(key, { count: 1, timestamp: now });
      return false;
    }

    if (now - entry.timestamp > this.RATE_LIMIT_WINDOW_MS) {
      this.rateLimitCache.set(key, { count: 1, timestamp: now });
      return false;
    }

    if (entry.count >= this.RATE_LIMIT_MAX_ATTEMPTS) {
      return true;
    }

    this.rateLimitCache.set(key, { count: entry.count + 1, timestamp: entry.timestamp });
    return false;
  }

  /**
   * Parse and categorize authentication errors
   * @param error The error to parse
   * @param context Additional context about the operation
   * @returns A structured auth error
   */
  private parseError(error: any, context?: Record<string, any>): AuthError {
    this.logEvent(AuthEvent.SESSION_ERROR, { error, context });
    
    const errorMessage = error?.message || error?.error_description || 'Unknown error occurred';
    
    // Network errors
    if (
      errorMessage.includes('Failed to fetch') || 
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('network')
    ) {
      return {
        type: AuthErrorType.NETWORK_ERROR,
        message: 'Network error. Please check your internet connection and try again.',
        originalError: error,
        context
      };
    }
    
    // Rate limit errors
    if (
      errorMessage.includes('rate limit') || 
      errorMessage.includes('429') ||
      error?.status === 429 ||
      error?.code === 'over_request_rate_limit'
    ) {
      return {
        type: AuthErrorType.RATE_LIMIT_EXCEEDED,
        message: 'Too many requests. Please wait a moment and try again.',
        originalError: error,
        context
      };
    }
    
    // Authentication errors
    if (errorMessage.includes('Invalid login credentials')) {
      return {
        type: AuthErrorType.INVALID_CREDENTIALS,
        message: 'Invalid email or password. Please check your credentials and try again.',
        originalError: error,
        context
      };
    }
    
    if (errorMessage.includes('Email not confirmed')) {
      return {
        type: AuthErrorType.EMAIL_NOT_CONFIRMED,
        message: 'Please check your email and confirm your account before signing in.',
        originalError: error,
        context
      };
    }
    
    if (errorMessage.includes('User already registered')) {
      return {
        type: AuthErrorType.USER_EXISTS,
        message: 'An account with this email already exists. Please sign in instead.',
        originalError: error,
        context
      };
    }

    if (isPasswordPolicyRejection(error)) {
      return {
        type: AuthErrorType.WEAK_PASSWORD,
        message: 'This password is too easy to guess or has appeared in a data breach. Choose a different, unique password.',
        originalError: error,
        context,
      };
    }
    
    // Default error
    return {
      type: AuthErrorType.UNKNOWN_ERROR,
      message: errorMessage,
      originalError: error,
      context
    };
  }

  /**
   * Retry an operation with exponential backoff
   * @param operation The operation to retry
   * @param maxRetries Maximum number of retry attempts
   * @param baseDelay Base delay in milliseconds
   * @returns The operation result
   */
  private async retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxRetries: number = this.MAX_RETRY_ATTEMPTS,
    baseDelay: number = 1500
  ): Promise<T> {
    let lastError: Error;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await operation();
      } catch (err) {
        lastError = err as Error;
        
        const errorMessage = err instanceof Error ? err.message : String(err);
        
        // Check if this is a rate limit error
        const isRateLimit = 
          errorMessage.includes('Request rate limit reached') || 
          errorMessage.includes('over_request_rate_limit') ||
          errorMessage.includes('rate limit') ||
          errorMessage.includes('429') ||
          (err && typeof err === 'object' && 'code' in err && (err as any).code === 'over_request_rate_limit') ||
          (err && typeof err === 'object' && 'status' in err && (err as any).status === 429);
        
        // Check if this is a network error
        const isNetworkError = 
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('fetch') ||
          errorMessage.includes('network') ||
          errorMessage.includes('ECONNRESET') ||
          errorMessage.includes('ETIMEDOUT');
        
        // Don't retry on authentication errors (except rate limits and network errors)
        if (err && typeof err === 'object' && 'code' in err) {
          const supabaseError = err as any;
             if (!isRateLimit && !isNetworkError &&
              (isPasswordPolicyRejection(err) ||
               supabaseError.code === 'invalid_grant' ||
               supabaseError.code === 'unauthorized' ||
               supabaseError.code === 'invalid_credentials' ||
               supabaseError.code === 'email_not_confirmed')) {
            throw err;
          }
        }
        
        // Log the retry reason
        if (isRateLimit) {
          this.logEvent(AuthEvent.SESSION_ERROR, {
            message: `Rate limit hit on attempt ${attempt + 1}, will retry...`,
            error: err
          });
        } else if (isNetworkError) {
          this.logEvent(AuthEvent.SESSION_ERROR, {
            message: `Network error on attempt ${attempt + 1}, will retry...`,
            error: err
          });
        } else {
          this.logEvent(AuthEvent.SESSION_ERROR, {
            message: `Retryable error on attempt ${attempt + 1}, will retry...`,
            error: err
          });
        }
        
        if (attempt < maxRetries - 1) {
          // Calculate delay based on error type
          let delay;
          if (isRateLimit) {
            // Longer delay for rate limits with more randomization
            const baseRateLimitDelay = Math.min(baseDelay * Math.pow(1.5, attempt), 8000);
            delay = baseRateLimitDelay + Math.random() * 1000; // Add jitter
          } else if (isNetworkError) {
            // Moderate delay for network errors
            delay = Math.min(baseDelay * Math.pow(1.3, attempt), 5000);
          } else {
            // Standard exponential backoff for other errors
            delay = Math.min(baseDelay * Math.pow(1.5, attempt), 6000);
          }
          
          this.logEvent(AuthEvent.SESSION_ERROR, {
            message: `Retrying in ${delay}ms (attempt ${attempt + 1} of ${maxRetries})`,
            delay
          });
          
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError!;
  }

  /**
   * Get the current authenticated user
   * @returns The current user or null if not authenticated
   */
  public async getCurrentUser(): Promise<User | null> {
    try {
      this.logEvent(AuthEvent.SESSION_REFRESH, { message: 'Getting current user' });
      
      const { data, error } = await this.retryWithBackoff(
        () => this.supabase.auth.getUser()
      );
      
      if (error) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Error getting current user', 
          error 
        });
        return null;
      }
      
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Current user retrieved', 
        userId: data.user?.id 
      });
      
      return data.user;
    } catch (error) {
      this.logEvent(AuthEvent.SESSION_ERROR, { 
        message: 'Exception getting current user', 
        error 
      });
      return null;
    }
  }

  /**
   * Get the current session
   * @returns The current session or null if not authenticated
   */
  public async getSession(): Promise<Session | null> {
    try {
      this.logEvent(AuthEvent.SESSION_REFRESH, { message: 'Getting current session' });
      
      const { data, error } = await this.retryWithBackoff(
        () => this.supabase.auth.getSession()
      );
      
      if (error) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Error getting session', 
          error 
        });
        return null;
      }
      
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Session retrieved', 
        hasSession: !!data.session 
      });
      
      return data.session;
    } catch (error) {
      this.logEvent(AuthEvent.SESSION_ERROR, { 
        message: 'Exception getting session', 
        error 
      });
      return null;
    }
  }

  /**
   * Refresh the current session
   * @returns The refreshed session or null if not authenticated
   */
  public async refreshSession(): Promise<Session | null> {
    try {
      this.logEvent(AuthEvent.SESSION_REFRESH, { message: 'Refreshing session' });
      
      const { data, error } = await this.retryWithBackoff(
        () => this.supabase.auth.refreshSession()
      );
      
      if (error) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Error refreshing session', 
          error 
        });
        return null;
      }
      
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Session refreshed', 
        hasSession: !!data.session 
      });
      
      return data.session;
    } catch (error) {
      this.logEvent(AuthEvent.SESSION_ERROR, { 
        message: 'Exception refreshing session', 
        error 
      });
      return null;
    }
  }

  /**
   * Sign in with email and password
   * @param params Login parameters
   * @returns Authentication response with user and session if successful
   */
  public async signIn(params: LoginParams): Promise<AuthResponse<{ user: User; session: Session }>> {
    const { email, password } = params;
    
    // Check for rate limiting
    const rateLimitKey = `signin:${email}`;
    if (this.isRateLimited(rateLimitKey)) {
      this.logEvent(AuthEvent.LOGIN_FAILURE, { 
        message: 'Rate limit exceeded for login', 
        email 
      });
      
      return {
        success: false,
        error: {
          type: AuthErrorType.RATE_LIMIT_EXCEEDED,
          message: 'Too many login attempts. Please try again later.'
        },
        statusCode: 429
      };
    }
    
    try {
      // Validate input
      if (!email || !password) {
        this.logEvent(AuthEvent.LOGIN_FAILURE, { 
          message: 'Missing email or password', 
          email: !!email 
        });
        
        return {
          success: false,
          error: {
            type: AuthErrorType.MISSING_FIELDS,
            message: 'Email and password are required'
          },
          statusCode: 400
        };
      }
      
      // Log login attempt
      this.logEvent(AuthEvent.ATTEMPT_LOGIN, { 
        message: 'Attempting login', 
        email 
      });
      
      // Attempt sign in with retry logic
      const { data, error } = await this.retryWithBackoff(
        () => this.supabase.auth.signInWithPassword({ email, password }),
        3, // Reduced retries to avoid rate limits
        2000 // Moderate base delay
      );
      
      // Handle sign in errors
      if (error) {
        this.logEvent(AuthEvent.LOGIN_FAILURE, { 
          message: 'Login failed', 
          error, 
          email 
        });
        
        return {
          success: false,
          error: this.parseError(error, { email }),
          statusCode: 401
        };
      }
      
      if (!data.user || !data.session) {
        this.logEvent(AuthEvent.LOGIN_FAILURE, { 
          message: 'No user or session returned', 
          email 
        });
        
        return {
          success: false,
          error: {
            type: AuthErrorType.UNKNOWN_ERROR,
            message: 'Failed to establish session'
          },
          statusCode: 500
        };
      }
      
      // Add a small delay to allow Supabase to fully process the session
      this.logEvent(AuthEvent.LOGIN_SUCCESS, { 
        message: 'Login successful, waiting for session to stabilize', 
        userId: data.user.id 
      });
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Explicitly refresh the session to ensure it's fully established
      const { data: sessionData, error: sessionError } = await this.retryWithBackoff(
        () => this.supabase.auth.getSession(),
        3, // Reduced retries to avoid rate limits
        1500 // Moderate base delay
      );
      
      if (sessionError) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Error refreshing session after login', 
          error: sessionError 
        });
        
        return {
          success: false,
          error: this.parseError(sessionError, { email }),
          statusCode: 500
        };
      }
      
      this.logEvent(AuthEvent.LOGIN_SUCCESS, { 
        message: 'Login and session refresh successful', 
        userId: data.user.id 
      });
      
      return {
        success: true,
        data: {
          user: data.user,
          session: sessionData.session || data.session
        },
        statusCode: 200
      };
    } catch (error) {
      this.logEvent(AuthEvent.LOGIN_FAILURE, { 
        message: 'Exception during login', 
        error, 
        email 
      });
      
      return {
        success: false,
        error: this.parseError(error, { email }),
        statusCode: 500
      };
    }
  }

  /**
   * Register a new user and salon
   * IMPORTANT: This method now only handles basic registration.
   * All salon_users creation is handled by the Edge Function to avoid conflicts.
   * 
   * @param params Registration parameters
   * @returns Authentication response with user and salon data if successful
   */
  public async signUp(params: RegisterParams): Promise<AuthResponse<{ user: User; salonId: string }>> {
    const rawEmail = params.email;
    const email = rawEmail.toLowerCase().trim();
    const { password, salonName, firstName, lastName, country, legalAcceptance } = params;
    const registrationStartedAt = performance.now();
    const logRegistrationTiming = (stage: string) => {
      console.info('registration_client_timing', {
        stage,
        elapsed_ms: Math.round(performance.now() - registrationStartedAt),
      });
    };

    // Normalize country code (ISO-2)
    const countryCode = (country ?? '').trim().toUpperCase() || null;

    // Use IANA timezone for "wall clock" booking behavior.
    // All timestamps remain stored as UTC in the DB.
    const salonTimezone = getTimezoneForCountry(countryCode ?? undefined);

    if (this.signingUp) {
      this.logEvent(AuthEvent.SIGNUP_FAILURE, { message: 'Signup already in progress', email });
      return { success: false, error: { type: AuthErrorType.CONFLICT, message: 'Signup already in progress' as any }, statusCode: 409 };
    }
    this.signingUp = true;

    let createdSalonId: string | null = null;
    let createdSalonDuringAttempt = false;
    let signedIn = false;

    try {
      // Rate limit & validation (unchanged)
      const rateLimitKey = `signup:${email}`;
      if (this.isRateLimited(rateLimitKey)) {
        return { success: false, error: { type: AuthErrorType.RATE_LIMIT_EXCEEDED, message: 'Too many signup attempts. Please try again later.' }, statusCode: 429 };
      }
      if (!email || !password || !salonName || !firstName || !lastName || !countryCode) {
        return { success: false, error: { type: AuthErrorType.MISSING_FIELDS, message: 'All required fields must be provided' }, statusCode: 400 };
      }
      if (!/^[A-Z]{2}$/.test(countryCode)) {
        return { success: false, error: { type: AuthErrorType.MISSING_FIELDS, message: 'Please provide a valid country code' }, statusCode: 400 };
      }
      if (!legalAcceptance?.accepted || !['web', 'mobile'].includes(legalAcceptance.source)) {
        return {
          success: false,
          error: { type: AuthErrorType.MISSING_FIELDS, message: 'Terms and Privacy Policy acceptance is required' },
          statusCode: 400,
        };
      }
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(email)) {
        return { success: false, error: { type: AuthErrorType.INVALID_EMAIL, message: 'Please provide a valid email address' }, statusCode: 400 };
      }
      if (password.length < 8) {
        return { success: false, error: { type: AuthErrorType.WEAK_PASSWORD, message: 'Password must be at least 8 characters long' }, statusCode: 400 };
      }

      // 1) Auth sign up first (no salon yet) so we become authenticated
      this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'Creating auth user', email });
      const { data: signUpData, error: signUpError } = await this.retryWithBackoff(
        () => this.supabase.auth.signUp({
          email,
          password,
          options: { data: { first_name: firstName, last_name: lastName } }
        }),
        3,
        2000
      );
      logRegistrationTiming('auth_signup_returned');
      if (signUpError && signUpError.code !== 'user_already_exists') {
        return { success: false, error: this.parseError(signUpError, { email }), statusCode: 500 };
      }
      if (signUpError?.code === 'user_already_exists') {
        this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'Resuming registration with existing auth identity' });
      } else if (!signUpData.user) {
        return { success: false, error: this.parseError(signUpError, { email }), statusCode: 500 };
      }

      // 2) Sign in (establish authenticated session). This also resumes an identity
      // created by a prior attempt that did not finish downstream provisioning.
      this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'Signing in registration identity', email });
      const { data: signInData, error: signInError } = await this.retryWithBackoff(
        () => this.supabase.auth.signInWithPassword({ email, password }),
        3,
        2000
      );
      if (signInError || !signInData.user || !signInData.session) {
        return { success: false, error: this.parseError(signInError, { email }), statusCode: 500 };
      }
      signedIn = true;
      logRegistrationTiming('auth_session_established');

      // Determine default currency (ISO-3) from DB helper; fallback to EUR.
      let currencyCode: string = 'EUR';
      try {
        if (countryCode) {
          const { data: currencyData, error: currencyError } = await this.supabase
            .rpc('currency_code_for_country', { _country_code: countryCode });
          if (!currencyError && typeof currencyData === 'string' && currencyData.trim()) {
            currencyCode = currencyData.trim().toUpperCase();
          }
        }
      } catch {
        // ignore and keep EUR
      }
      logRegistrationTiming('currency_resolved');

      // 3) Reuse a salon left by an incomplete attempt; otherwise create one now.
      const { data: existingSalon } = await this.retryWithBackoff(
        async () => {
          const { data, error } = await this.supabase
            .from('salons')
            .select('id')
            .eq('email', email)
            .maybeSingle();
          if (error) throw error;
          return { data };
        },
        2,
        1000,
      );

      if (existingSalon?.id) {
        createdSalonId = existingSalon.id;
        this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'Resuming existing salon registration', salonId: createdSalonId });
      } else {
      this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'Creating salon (authenticated)', salonName });
      const trialStartsAt = new Date();
      const trialEndsAt = getTrialEndDate(trialStartsAt);
      const { data: salonInsertData, error: salonError } = await this.retryWithBackoff(
        async () => this.supabase
          .from('salons')
          .insert([{
            name: salonName,
            email: email,
            phone: null,
            address: null,

            // new explicit columns
            country_code: countryCode,
            currency_code: currencyCode,

            // legacy JSON (temporary back-compat)
            settings: { country: countryCode, currency: currencyCode, timezone: salonTimezone },

            billing_status: 'trialing',
            plan_code: 'SOLO',
            trial_starts_at: trialStartsAt.toISOString(),
            trial_ends_at: trialEndsAt.toISOString(),
            seat_limit: 1,
            stripe_customer_id: null,
            stripe_subscription_id: null
          }])
          .select('id')
          .single(),
        3,
        1500
      );
      if (salonError || !salonInsertData?.id) {
        if (salonError?.code === '23505') {
          return { success: false, error: { type: AuthErrorType.USER_EXISTS, message: 'An account with this email already exists. Please sign in instead.' }, statusCode: 409 };
        }
        return { success: false, error: this.parseError(salonError, { salonName, email }), statusCode: 500 };
      }
      createdSalonId = salonInsertData.id;
      createdSalonDuringAttempt = true;
      this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'Salon created (authenticated)', salonId: createdSalonId });
      }
      logRegistrationTiming('salon_ready');

      // 4) Call Edge Function to create salon_users admin record
      this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'Calling Edge Function for salon_users association', salonId: createdSalonId });
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const edgeFunctionUrl = `${supabaseUrl}/functions/v1/complete-registration`;
        const session = signInData.session;
        if (!session?.access_token) throw new Error('Missing access token');
        const resp = await fetch(edgeFunctionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ salonId: createdSalonId, firstName, lastName, email, legalAcceptance })
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`Edge function failed (${resp.status}): ${txt}`);
        }
        const result = await resp.json();
        if (!result.success) throw new Error(result.error || 'Edge function returned failure');
        this.logEvent(AuthEvent.ATTEMPT_SIGNUP, { message: 'salon_users admin created', salonUserId: result.salonUserId });
        logRegistrationTiming('provisioning_completed');
      } catch (efErr) {
        // Cleanup: delete salon we just created
        if (createdSalonId && createdSalonDuringAttempt) await this.supabase.from('salons').delete().eq('id', createdSalonId);
        await this.supabase.auth.signOut();
        return { success: false, error: this.parseError(efErr, { email }), statusCode: 500 };
      }

      const completedSalonId = createdSalonId as string;
      this.logEvent(AuthEvent.SIGNUP_SUCCESS, { message: 'Registration completed', salonId: createdSalonId });
      trackEventOncePerSession('sign_up_completed', registrationCompletedSessionKey(signInData.user.id, completedSalonId), {
        method: 'email',
        source: 'web_app',
      });
      // The successful password sign-in already returned the session and user used for provisioning.
      return { success: true, data: { user: signInData.user, salonId: completedSalonId }, statusCode: 201 };

    } catch (error) {
      // If salon was created but later failure (unlikely here), attempt cleanup
      if (createdSalonId && createdSalonDuringAttempt) {
        try { await this.supabase.from('salons').delete().eq('id', createdSalonId); } catch { /* ignore */ }
      }
      if (signedIn) { try { await this.supabase.auth.signOut(); } catch { /* ignore */ } }
      return { success: false, error: this.parseError(error, { email, salonName }), statusCode: 500 };
    } finally {
      this.signingUp = false;
    }
  }

  /**
   * Sign out the current user
   * @returns Success status
   */
  public async signOut(): Promise<AuthResponse> {
    try {
      this.logEvent(AuthEvent.LOGOUT, { message: 'Signing out user' });
      
      const { error } = await this.supabase.auth.signOut();
      
      if (error) {
        this.logEvent(AuthEvent.LOGOUT, { 
          message: 'Error during sign out', 
          error 
        });
        
        return {
          success: false,
          error: this.parseError(error),
          statusCode: 500
        };
      }
      
      this.logEvent(AuthEvent.LOGOUT, { message: 'Sign out successful' });
      
      return {
        success: true,
        statusCode: 200
      };
    } catch (error) {
      this.logEvent(AuthEvent.LOGOUT, { 
        message: 'Exception during sign out', 
        error 
      });
      
      return {
        success: false,
        error: this.parseError(error),
        statusCode: 500
      };
    }
  }

  /**
   * Request a password reset email
   * @param params Password reset parameters
   * @returns Success status
   */
  public async resetPassword(params: PasswordResetParams): Promise<AuthResponse> {
    const { email } = params;

    if (!email) {
      return {
        success: false,
        error: {
          type: AuthErrorType.MISSING_FIELDS,
          message: 'Email is required',
        },
        statusCode: 400,
      };
    }

    // Rate limiting by email
    const rateLimitKey = `reset_password:${email}`;
    if (this.isRateLimited(rateLimitKey)) {
      return {
        success: false,
        error: {
          type: AuthErrorType.RATE_LIMIT_EXCEEDED,
          message: 'Too many password reset attempts. Please try again later.',
        },
        statusCode: 429,
      };
    }

    try {
      this.logEvent(AuthEvent.SESSION_REFRESH, {
        message: 'Requesting password reset',
        email,
      });

      // Option 1 (chosen): Always request the reset email via Supabase Auth.
      // Do not query `salon_users` here: this runs while unauthenticated and RLS will block anon reads,
      // causing false negatives and no email.
      const { error: resetError } = await this.retryWithBackoff(
        () =>
          this.supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/password-setup`,
          }),
        3,
        2000
      );

      if (resetError) {
        this.logEvent(AuthEvent.SESSION_ERROR, {
          message: 'Password reset error',
          error: resetError,
        });

        // Return generic success to avoid user enumeration.
        return {
          success: true,
          statusCode: 200,
        };
      }

      this.logEvent(AuthEvent.SESSION_REFRESH, {
        message: 'Password reset email requested successfully',
        email,
      });

      return {
        success: true,
        statusCode: 200,
      };
    } catch (error) {
      this.logEvent(AuthEvent.SESSION_ERROR, {
        message: 'Exception during password reset',
        error,
        email,
      });

      // Return generic success to avoid user enumeration.
      return {
        success: true,
        statusCode: 200,
      };
    }
  }

  /**
   * Verify a password reset token
   * @param params Password verification parameters
   * @returns Success status and user data if valid
   */
  public async verifyPasswordToken(params: PasswordVerifyParams): Promise<AuthResponse<{ valid: boolean; user?: User }>> {
    const { token, email } = params;
    
    try {
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Verifying password token', 
        email 
      });
      
      // Verify the token
      const { data, error } = await this.retryWithBackoff(
        () => this.supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery',
          email
        }),
        3,
        1500
      );
      
      if (error) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Password token verification error', 
          error 
        });
        
        return {
          success: false,
          error: this.parseError(error, { email }),
          statusCode: 401
        };
      }
      
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Password token verified successfully', 
        userId: data.user?.id 
      });
      
      return {
        success: true,
        data: {
          valid: true,
          user: data.user ?? undefined
        },
        statusCode: 200
      };
    } catch (error) {
      this.logEvent(AuthEvent.SESSION_ERROR, { 
        message: 'Exception during password token verification', 
        error, 
        email 
      });
      
      return {
        success: false,
        error: this.parseError(error, { email }),
        statusCode: 500
      };
    }
  }

  /**
   * Set a new password after reset
   * @param params Password update parameters
   * @returns Success status
   */
  public async setPassword(params: PasswordUpdateParams): Promise<AuthResponse> {
    const { email, password, token } = params;
    
    try {
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Setting new password', 
        email 
      });
      
      // Verify token first
      const verifyResult = await this.verifyPasswordToken({ token, email });
      
      if (!verifyResult.success || !verifyResult.data?.valid) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Token verification failed during password reset', 
          error: verifyResult.error 
        });
        
        return {
          success: false,
          error: verifyResult.error || {
            type: AuthErrorType.INVALID_CREDENTIALS,
            message: 'Invalid or expired token'
          },
          statusCode: 401
        };
      }
      
      // Update the password
      const { error } = await this.retryWithBackoff(
        () => this.supabase.auth.updateUser({
          password
        }),
        3,
        1500
      );
      
      if (error) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Error updating password', 
          error 
        });
        
        return {
          success: false,
          error: this.parseError(error, { email }),
          statusCode: 500
        };
      }
      
      // Update the salon_user record
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Updating salon_user record', 
        email 
      });
      
      const { error: updateError } = await this.retryWithBackoff(
        async () => this.supabase
          .from('salon_users')
          .update({
            encrypted_password: password, // This will be hashed by the trigger
            email_confirmed_at: new Date().toISOString()
          })
          .eq('email', email),
        3,
        1500
      );
      
      if (updateError) {
        this.logEvent(AuthEvent.SESSION_ERROR, { 
          message: 'Error updating salon_user record', 
          error: updateError 
        });
        
        return {
          success: false,
          error: this.parseError(updateError, { email }),
          statusCode: 500
        };
      }
      
      this.logEvent(AuthEvent.SESSION_REFRESH, { 
        message: 'Password set successfully', 
        email 
      });
      
      return {
        success: true,
        statusCode: 200
      };
    } catch (error) {
      this.logEvent(AuthEvent.SESSION_ERROR, { 
        message: 'Exception while setting password', 
        error, 
        email 
      });
      
      return {
        success: false,
        error: this.parseError(error, { email }),
        statusCode: 500
      };
    }
  }

  /**
   * Get the Supabase client instance
   * @returns The Supabase client
   */
  public getClient(): SupabaseClient<Database> {
    return this.supabase;
  }
}

// Create and export a singleton instance using singleton client
export const authMiddleware = new AuthMiddleware(
  import.meta.env.MODE === 'development' // Enable debug mode in development
);